// src/components/PortfolioTable.jsx
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { sectorOf } from "../lib/sectors";
import { SIDE_BOOK_KRW } from "../lib/exposure";
import { krMarketOf, krMarketLabel, krMarketColor } from "../lib/krMarket";

function fmt(n, d = 2) {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtKRW(n) {
  if (n == null || isNaN(n)) return "—";
  return "₩" + Math.round(n).toLocaleString("ko-KR");
}
const MARKET_BADGE = {
  US: { bg: "#e8f4fd", color: "#1565c0", label: "🇺🇸 US" },
  TW: { bg: "#e8f5e9", color: "#2e7d32", label: "🇹🇼 TW" },
  JP: { bg: "#fce4ec", color: "#c62828", label: "🇯🇵 JP" },
  HK: { bg: "#fff8e1", color: "#e65100", label: "🇭🇰 HK" },
  KR: { bg: "#eef2ff", color: "#3730a3", label: "🇰🇷 KR" },
};
function getCurrentFxToKRW(currency, rates) {
  if (currency === "KRW") return 1;   // 원화는 KRW→KRW = 1 (기본 통화)
  if (currency === "USD") return rates.KRW || 1380;
  if (currency === "JPY") return (rates.KRW || 1380) / (rates.JPY || 149);
  if (currency === "HKD") return (rates.KRW || 1380) / (rates.HKD || 7.8);
  if (currency === "TWD") return (rates.KRW || 1380) / (rates.TWD || 31.5);
  return rates.KRW || 1380;
}
const currencySymbol = c => ({ USD: "$", JPY: "¥", HKD: "HK$", TWD: "NT$", KRW: "₩" }[c] || "$");

// 앵커 대비 드리프트 — ±0.3%p 이내는 노이즈로 보고 회색
const DRIFT_DEAD = 0.3;
function driftColor(d) {
  if (d == null || isNaN(d)) return "#bbb";
  if (Math.abs(d) < DRIFT_DEAD) return "#bbb";
  return d > 0 ? "#c0392b" : "#1f5fd0";
}
function anchorTitle(row) {
  if (row.revertQty == null) return "최초(장부원가) 기준 비중";
  const q = Math.round(row.revertQty);
  if (q === 0) return "앵커 비중 — 원복 불필요";
  return q < 0
    ? `앵커로 원복: ${Math.abs(q).toLocaleString()}주 매도`
    : `앵커로 원복: ${q.toLocaleString()}주 매수`;
}

export default function PortfolioTable({ holdings, marketData, exchangeRates, onUpdate, onDelete }) {
  const [editingTicker, setEditingTicker] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sortBy, setSortBy] = useState("weight");
  const [sortDir, setSortDir] = useState("desc");
  const [sideFilter, setSideFilter] = useState("all");   // all | long | short
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const rates = { KRW: 1380, JPY: 149, TWD: 31.5, HKD: 7.8, ...exchangeRates };

  const rows = holdings.map(h => {
    const md = marketData[h.ticker];
    const currency = md?.currency || h.currency || "USD";
    const currentFxToKRW = getCurrentFxToKRW(currency, rates);
    const bookFxToKRW = +h.avgExchangeRate > 0 ? +h.avgExchangeRate : currentFxToKRW;
    const mult = String(h.notes || "").includes("단선") ? 10 : 1;   // 단선 노미널 ×10 (계약수 표시는 유지)
    const hasPrice = md && md.price > 0;
    const currentPrice = hasPrice ? md.price : +h.avgCost;
    const valueForeign = currentPrice * h.quantity * mult;
    const valueKRW = valueForeign * currentFxToKRW;
    const costForeign = +h.avgCost * h.quantity * mult;
    const costKRW = costForeign * bookFxToKRW;
    const short = String(h.side || "Long").toLowerCase().startsWith("s");
    const m = short ? -1 : 1;
    const pnlKRW = hasPrice ? m * (valueKRW - costKRW) : 0;  // 숏: 가격하락 시 +
    const pnlPct = hasPrice && costKRW > 0 ? (pnlKRW / costKRW) * 100 : 0;
    const valueUSD = hasPrice ? md.priceUSD * h.quantity * mult : costForeign / (rates[currency] || 1);
    const weight = (valueKRW / SIDE_BOOK_KRW) * 100;   // 고정 북사이즈(80억) 대비
    // 앵커 = 장부원가(=최초 입력 시점) 기준 비중. 가격이 튀거나 빠져도 움직이지 않는 기준선.
    const initWeight = (costKRW / SIDE_BOOK_KRW) * 100;
    const drift = weight - initWeight;                 // +면 튄 것, -면 빠진 것
    // 앵커 비중으로 되돌리는 데 필요한 수량 증감 (음수=매도)
    const revertQty = hasPrice && currentPrice > 0
      ? costKRW / (currentPrice * mult * currentFxToKRW) - h.quantity : null;
    const changePct = md?.changePct;
    // 일간 수익기여 = (숏이면 -1) × 비중% × 등락률% / 100  (숏은 하락 시 +기여)
    const contribution = hasPrice && changePct != null ? m * weight * changePct / 100 : null;
    return {
      ...h, currency, currentPrice, currentFxToKRW, bookFxToKRW, short,
      valueForeign, valueKRW, costForeign, costKRW, pnlKRW, pnlPct,
      valueUSD, weight, initWeight, drift, revertQty, hasPrice, contribution,
      changePct, pe: md?.pe, divYield: md?.divYield,
      name: md?.name || h.name || h.ticker,
      marketState: md?.marketState,
      preMarketPrice: md?.preMarketPrice,
      preMarketChangePercent: md?.preMarketChangePercent,
      postMarketPrice: md?.postMarketPrice,
      postMarketChangePercent: md?.postMarketChangePercent,
    };
  });

  // 시간 외 세션 표시 정보 — 미국주식에서 marketState 또는 데이터 유무로 판단
  function pickExtended(row) {
    if (!row.hasPrice || row.market !== "US") return null;
    const s = row.marketState;
    if ((s === "PRE" || s === "PREPRE") && row.preMarketPrice) {
      return { label: "Pre:", price: row.preMarketPrice, pct: row.preMarketChangePercent };
    }
    if ((s === "POST" || s === "POSTPOST") && row.postMarketPrice) {
      return { label: "After:", price: row.postMarketPrice, pct: row.postMarketChangePercent };
    }
    if (row.postMarketPrice) return { label: "After:", price: row.postMarketPrice, pct: row.postMarketChangePercent };
    if (row.preMarketPrice)  return { label: "Pre:", price: row.preMarketPrice, pct: row.preMarketChangePercent };
    return null;
  }

  const sorted = [...rows].sort((a, b) => {
    let va = a[sortBy] ?? -Infinity, vb = b[sortBy] ?? -Infinity;
    return sortDir === "asc" ? va - vb : vb - va;
  });
  const longN = rows.filter(r => !r.short).length;
  const shortN = rows.length - longN;
  const visible = sideFilter === "all" ? sorted : sorted.filter(r => sideFilter === "short" ? r.short : !r.short);

  const handleSort = col => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const startEdit = row => {
    setEditingTicker(row.ticker);
    setEditForm({ ticker: row.ticker, name: row.name || "", quantity: row.quantity, avgCost: row.avgCost, currency: row.currency, avgExchangeRate: row.avgExchangeRate || "", notes: row.notes || "" });
  };
  const saveEdit = row => {
    onUpdate({ ...row, ...editForm, _oldTicker: row.ticker, quantity: +editForm.quantity, avgCost: +editForm.avgCost, avgExchangeRate: editForm.avgExchangeRate ? +editForm.avgExchangeRate : undefined });
    setEditingTicker(null);
  };

  const totalContribution = rows.reduce((s, r) => s + (r.contribution || 0), 0);

  // 이미지 양식(LONG | SHORT 좌우 분할)대로 엑셀 다운로드
  const exportExcel = () => {
    const p2 = n => (n == null || isNaN(n)) ? "" : Number(n.toFixed(2));
    const longs = rows.filter(r => !r.short).sort((a, b) => b.weight - a.weight);
    const shorts = rows.filter(r => r.short).sort((a, b) => b.weight - a.weight);
    const n = Math.max(longs.length, shorts.length);
    const aoa = [["LONG 종목", "종목코드", "비중(%)", "DAILY 등락률(%)", "수익기여(%)", "",
                  "SHORT 종목", "종목코드", "비중(%)", "DAILY 등락률(%)", "수익기여(%)"]];
    for (let i = 0; i < n; i++) {
      const L = longs[i], S = shorts[i];
      aoa.push([
        L ? L.name : "", L ? L.ticker : "", L ? p2(L.weight) : "", L ? p2(L.changePct) : "", L ? p2(L.contribution) : "",
        "",
        S ? S.name : "", S ? S.ticker : "", S ? p2(S.weight) : "", S ? p2(S.changePct) : "", S ? p2(S.contribution) : "",
      ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [14, 9, 8, 14, 11, 2, 14, 9, 8, 14, 11].map(wch => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "포트폴리오");
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    XLSX.writeFile(wb, `longshort_portfolio_${stamp}.xlsx`);
  };

  const exportBtnStyle = {
    fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8,
    border: "1px solid #d0d0d8", background: "#fff", color: "#1a1a2e", cursor: "pointer", whiteSpace: "nowrap",
  };

  const SideFilter = () => (
    <div style={{ display: "flex", border: "1px solid #e2e2e2", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
      {[["all", `전체 ${rows.length}`], ["long", `롱 ${longN}`], ["short", `숏 ${shortN}`]].map(([v, lab]) => (
        <button key={v} onClick={() => setSideFilter(v)} style={{
          padding: "5px 10px", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
          background: sideFilter === v ? "#1a1a2e" : "#fff",
          color: sideFilter === v ? "#fff" : (v === "long" ? "#0a7c59" : v === "short" ? "#c0392b" : "#888"),
        }}>{lab}</button>
      ))}
    </div>
  );

  // ── 모바일 카드 뷰 ───────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>보유 종목</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <SideFilter />
            <span style={{ fontSize: 11, fontWeight: 700, color: totalContribution >= 0 ? "#0a7c59" : "#c0392b" }}>
              기여 {totalContribution >= 0 ? "+" : ""}{fmt(totalContribution, 2)}%
            </span>
            <button onClick={exportExcel} style={exportBtnStyle}>📥 엑셀</button>
          </div>
        </div>
        {visible.map(row => {
          const isEditing = editingTicker === row.ticker;
          const badge = MARKET_BADGE[row.market] || MARKET_BADGE.US;
          const sym = currencySymbol(row.currency);
          const pnlColor = row.pnlPct >= 0 ? "#0a7c59" : "#c0392b";

          return (
            <div key={row.ticker} style={{ padding: "14px 16px", borderBottom: "1px solid #f5f5f5", background: isEditing ? "#fffbf0" : "transparent" }}>
              {/* 상단: 종목명 + 수익률 */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: badge.bg, color: badge.color, fontWeight: 600 }}>
                    {badge.label}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e", display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 700, background: row.short ? "#fdecea" : "#e7f5ec", color: row.short ? "#c0392b" : "#0a7c59" }}>{row.short ? "S" : "L"}</span>
                      {isEditing ? (
                        <input value={editForm.ticker} onChange={e => setEditForm(f => ({ ...f, ticker: e.target.value }))}
                          placeholder="종목코드" style={{ width: 90, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13 }} />
                      ) : row.ticker}
                    </div>
                    {isEditing ? (
                      <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="종목명" style={{ marginTop: 2, width: 130, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
                    ) : (
                      <div style={{ fontSize: 11, color: "#aaa" }}>{row.name}</div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: pnlColor }}>
                    {row.hasPrice ? `${row.pnlPct >= 0 ? "+" : ""}${fmt(row.pnlPct, 2)}%` : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: pnlColor }}>
                    {row.hasPrice ? `${row.pnlKRW >= 0 ? "+" : ""}${fmtKRW(row.pnlKRW)}` : "—"}
                  </div>
                </div>
              </div>

              {/* 중단: 가격 정보 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div style={{ background: "#f8f8f8", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 2 }}>현재가</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {row.hasPrice ? `${sym}${fmt(row.currentPrice, 2)}` : "—"}
                  </div>
                  <div style={{ fontSize: 10, color: row.changePct >= 0 ? "#0a7c59" : "#c0392b" }}>
                    {row.hasPrice && row.changePct != null ? `${row.changePct >= 0 ? "+" : ""}${fmt(row.changePct, 2)}%` : ""}
                  </div>
                  {(() => {
                    const ext = pickExtended(row);
                    if (!ext) return null;
                    const c = (ext.pct || 0) >= 0 ? "#0a7c59" : "#c0392b";
                    return (
                      <div style={{ fontSize: 9, marginTop: 3, color: c, lineHeight: 1.3 }}>
                        {ext.label} ${fmt(ext.price, 2)}{ext.pct != null && ` (${ext.pct >= 0 ? "+" : ""}${fmt(ext.pct, 2)}%)`}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ background: "#f8f8f8", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 2 }}>평가금액</div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{fmtKRW(row.valueKRW)}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>
                    {fmt(row.weight, 1)}% <span style={{ color: "#ccc" }}>/ 최초 {fmt(row.initWeight, 1)}%</span>
                    <span style={{ marginLeft: 4, fontWeight: 700, color: driftColor(row.drift) }}>
                      {row.drift >= 0 ? "+" : ""}{fmt(row.drift, 1)}%p
                    </span>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: (row.contribution || 0) >= 0 ? "#0a7c59" : "#c0392b" }}>
                    기여 {row.contribution != null ? `${row.contribution >= 0 ? "+" : ""}${fmt(row.contribution, 2)}%` : "—"}
                  </div>
                </div>
                <div style={{ background: "#f8f8f8", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 2 }}>수량</div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{fmt(row.quantity, 0)}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>{sym}{fmt(row.avgCost, 2)}</div>
                </div>
              </div>

              {/* 편집 모드 */}
              {isEditing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input type="number" value={editForm.quantity} onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))}
                      placeholder="수량" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13 }} />
                    <input type="number" value={editForm.avgCost} onChange={e => setEditForm(f => ({ ...f, avgCost: e.target.value }))}
                      placeholder={`장부원가(${row.currency})`} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13 }} />
                  </div>
                  <input type="number" value={editForm.avgExchangeRate} onChange={e => setEditForm(f => ({ ...f, avgExchangeRate: e.target.value }))}
                    placeholder="장부환율(→KRW)" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, width: "100%", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => saveEdit(row)} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", background: "#1a1a2e", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>저장</button>
                    <button onClick={() => setEditingTicker(null)} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer" }}>취소</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => startEdit(row)} style={{ flex: 1, padding: "7px", borderRadius: 8, border: "1px solid #e8e8e8", background: "#fff", fontSize: 12, cursor: "pointer", color: "#555" }}>✏️ 편집</button>
                  <button onClick={() => onDelete(row.ticker)} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #fdd", background: "#fff5f5", fontSize: 12, cursor: "pointer", color: "#c0392b" }}>🗑</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── 데스크탑 테이블 뷰 (기존) ───────────────────────────────
  const Col = ({ col, label, left }) => (
    <th onClick={() => handleSort(col)} style={{
      padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888",
      textAlign: left ? "left" : "right", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
    }}>
      {label} {sortBy === col ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>보유 종목</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <SideFilter />
          <span style={{ fontSize: 12, fontWeight: 700, color: totalContribution >= 0 ? "#0a7c59" : "#c0392b" }}>
            일간 수익기여 {totalContribution >= 0 ? "+" : ""}{fmt(totalContribution, 2)}%
          </span>
          <span style={{ fontSize: 12, color: "#aaa" }}>{holdings.length}개 종목</span>
          <button onClick={exportExcel} style={exportBtnStyle}>📥 엑셀</button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
              <Col col="ticker" label="종목" left />
              <Col col="weight" label="비중(%)" />
              <Col col="drift" label={<>최초비중(%)<br/><span style={{ fontWeight: 400, color: "#bbb" }}>드리프트</span></>} />
              <Col col="quantity" label="수량" />
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "right", whiteSpace: "nowrap" }}>장부원가<br/><span style={{ fontWeight: 400, color: "#bbb" }}>장부환율</span></th>
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "right", whiteSpace: "nowrap" }}>현재가<br/><span style={{ fontWeight: 400, color: "#bbb" }}>평가환율</span></th>
              <Col col="changePct" label="등락률" />
              <Col col="contribution" label="수익기여" />
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "right", whiteSpace: "nowrap" }}>평가금액(외화)<br/><span style={{ fontWeight: 400, color: "#bbb" }}>평가금액(원화)</span></th>
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "right", whiteSpace: "nowrap" }}>장부금액(외화)<br/><span style={{ fontWeight: 400, color: "#bbb" }}>장부금액(원화)</span></th>
              <Col col="pnlPct" label="수익률" />
              <Col col="pnlKRW" label="손익(원화)" />
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "center", whiteSpace: "nowrap" }}>섹터</th>
              <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: "center" }}>편집</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(row => {
              const isEditing = editingTicker === row.ticker;
              const badge = MARKET_BADGE[row.market] || MARKET_BADGE.US;
              const sym = currencySymbol(row.currency);
              return (
                <tr key={row.ticker} style={{ borderBottom: "1px solid #f8f8f8", background: isEditing ? "#fffbf0" : "transparent" }}>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: badge.bg, color: badge.color, fontWeight: 600 }}>{badge.label}</span>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 700, background: row.short ? "#fdecea" : "#e7f5ec", color: row.short ? "#c0392b" : "#0a7c59" }}>{row.short ? "SHORT" : "LONG"}</span>
                          {isEditing ? (
                            <input value={editForm.ticker} onChange={e => setEditForm(f => ({ ...f, ticker: e.target.value }))}
                              placeholder="종목코드" style={{ width: 80, padding: "1px 5px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
                          ) : (
                            <span style={{ fontWeight: 700, color: "#1a1a2e" }}>{row.ticker}</span>
                          )}
                          {!row.hasPrice && <span style={{ fontSize: 9, padding: "1px 4px", background: "#fff3e0", color: "#e65100", borderRadius: 3, fontWeight: 600 }}>수기</span>}
                        </div>
                        {isEditing ? (
                          <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="종목명" style={{ width: 120, padding: "1px 5px", border: "1px solid #ddd", borderRadius: 4, fontSize: 11 }} />
                        ) : (
                          <div style={{ fontSize: 10, color: "#aaa", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }}>{row.name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                      <div style={{ width: 40, height: 4, background: "#f0f0f0", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(row.weight, 100)}%`, height: "100%", background: "#1a1a2e", borderRadius: 2 }} />
                      </div>
                      <span style={{ fontWeight: 600 }}>{fmt(row.weight, 1)}%</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }} title={anchorTitle(row)}>
                    <div style={{ color: "#666" }}>{fmt(row.initWeight, 1)}%</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: driftColor(row.drift) }}>
                      {row.drift >= 0 ? "+" : ""}{fmt(row.drift, 1)}%p
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    {isEditing ? (
                      <input type="number" value={editForm.quantity} onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))}
                        style={{ width: 70, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13, textAlign: "right" }} />
                    ) : fmt(row.quantity, 0)}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#555" }}>
                    {isEditing ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                        <input type="number" value={editForm.avgCost} onChange={e => setEditForm(f => ({ ...f, avgCost: e.target.value }))}
                          placeholder={`단가(${row.currency})`} style={{ width: 90, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12, textAlign: "right" }} />
                        <input type="number" value={editForm.avgExchangeRate} onChange={e => setEditForm(f => ({ ...f, avgExchangeRate: e.target.value }))}
                          placeholder="장부환율(KRW)" style={{ width: 90, padding: "2px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12, textAlign: "right" }} />
                      </div>
                    ) : (
                      <div>
                        <div>{sym}{fmt(row.avgCost, 2)}</div>
                        <div style={{ fontSize: 10, color: "#bbb" }}>
                          {row.currency === "KRW" ? "원화(1:1)"
                            : row.avgExchangeRate ? `₩${fmt(+row.avgExchangeRate, 1)}`
                            : <span style={{ color: "#f0a500" }}>환율 미입력</span>}
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500 }}>
                    <div>
                      <div style={{ color: row.hasPrice ? "#1a1a2e" : "#aaa" }}>{row.hasPrice ? `${sym}${fmt(row.currentPrice, 2)}` : "—"}</div>
                      {row.hasPrice && <div style={{ fontSize: 10, color: "#bbb" }}>₩{fmt(row.currentFxToKRW, 1)}</div>}
                      {(() => {
                        const ext = pickExtended(row);
                        if (!ext) return null;
                        const c = (ext.pct || 0) >= 0 ? "#0a7c59" : "#c0392b";
                        return (
                          <div style={{ fontSize: 10, color: c, marginTop: 2, fontWeight: 500 }}>
                            {ext.label} ${fmt(ext.price, 2)}{ext.pct != null && ` (${ext.pct >= 0 ? "+" : ""}${fmt(ext.pct, 2)}%)`}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: (row.changePct || 0) >= 0 ? "#0a7c59" : "#c0392b", fontWeight: 600 }}>
                    {row.hasPrice && row.changePct != null ? `${row.changePct >= 0 ? "+" : ""}${fmt(row.changePct, 2)}%` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: (row.contribution || 0) >= 0 ? "#0a7c59" : "#c0392b", fontWeight: 600 }}>
                    {row.contribution != null ? `${row.contribution >= 0 ? "+" : ""}${fmt(row.contribution, 2)}%` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <div>{sym}{fmt(row.valueForeign, 0)}</div>
                    <div style={{ fontSize: 10, color: "#888" }}>{fmtKRW(row.valueKRW)}</div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#555" }}>
                    <div>{sym}{fmt(row.costForeign, 0)}</div>
                    <div style={{ fontSize: 10, color: "#bbb" }}>{fmtKRW(row.costKRW)}</div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: row.pnlPct >= 0 ? "#0a7c59" : "#c0392b", fontWeight: 600 }}>
                    {row.hasPrice ? `${row.pnlPct >= 0 ? "+" : ""}${fmt(row.pnlPct, 2)}%` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: row.pnlKRW >= 0 ? "#0a7c59" : "#c0392b" }}>
                    {row.hasPrice ? `${row.pnlKRW >= 0 ? "+" : ""}${fmtKRW(row.pnlKRW)}` : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 10.5, color: "#666", padding: "2px 7px", borderRadius: 4, background: "#f2f2f4" }}>{sectorOf(row.ticker, row.market)}</span>
                    {(() => {
                      const km = krMarketOf(row, marketData);
                      return km ? (
                        <span style={{
                          fontSize: 10, fontWeight: 700, marginLeft: 4, padding: "2px 6px", borderRadius: 4,
                          color: krMarketColor(km), background: km === "KS" ? "#eef2fb" : "#fdf3e6",
                        }}>{krMarketLabel(km)}</span>
                      ) : null;
                    })()}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <button onClick={() => saveEdit(row)} style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: "none", background: "#1a1a2e", color: "#fff", cursor: "pointer" }}>저장</button>
                        <button onClick={() => setEditingTicker(null)} style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: "1px solid #ddd", background: "#fff", cursor: "pointer" }}>취소</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <button onClick={() => startEdit(row)} style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: "1px solid #ddd", background: "#fff", cursor: "pointer", color: "#555" }}>✏️</button>
                        <button onClick={() => onDelete(row.ticker)} style={{ padding: "3px 8px", fontSize: 11, borderRadius: 4, border: "1px solid #fdd", background: "#fff5f5", cursor: "pointer", color: "#c0392b" }}>🗑</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
